import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useForm, type Resolver, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { AlertCircle, ChevronDown, Loader2, Lock } from 'lucide-react';
import {
  STORE_DESCRIPTION_MAX_LENGTH,
  buildStoreUrl,
  deleteStoreLogoByUrl,
  deleteStoreLogoObject,
  mapStoreDetailsError,
  stageStoreLogoReplacement,
  updateStoreDetailsSchema,
  useTranslation,
  useUpdateStoreDetails,
} from '@dukanti/shared';
import type {
  Locality,
  Store,
  StoreDetailsErrorKey,
  StoreDetailsFormValues,
  StoreLogoChange,
  TranslationKey,
} from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/lib/supabase';
import { useNavBlocker } from '@/hooks/use-nav-blocker';
import { StoreLogoField } from './store-logo-field';

export interface StoreDetailsFormProps {
  readonly store: Store;
  readonly localities: readonly Locality[];
  readonly localitiesLoading?: boolean;
  readonly localitiesError?: boolean;
}

const storeDetailsResolver = zodResolver(
  updateStoreDetailsSchema,
) as Resolver<StoreDetailsFormValues>;

export function StoreDetailsForm({
  store,
  localities,
  localitiesLoading = false,
  localitiesError = false,
}: StoreDetailsFormProps) {
  const { t, dir, lang } = useTranslation();
  const updateMutation = useUpdateStoreDetails(supabase);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [removeRequested, setRemoveRequested] = useState(false);
  const [logoError, setLogoError] = useState<StoreDetailsErrorKey | null>(null);
  const [submitError, setSubmitError] = useState<StoreDetailsErrorKey | null>(null);
  const publicUrl = useMemo(() => buildStoreUrl(store.slug), [store.slug]);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm<StoreDetailsFormValues>({
    resolver: storeDetailsResolver,
    mode: 'onBlur',
    defaultValues: {
      name: store.name,
      description: store.description ?? '',
      localityId: store.locality_id ?? '',
    },
  });

  useNavBlocker(isDirty || selectedFile !== null || removeRequested);

  useEffect(() => {
    reset({
      name: store.name,
      description: store.description ?? '',
      localityId: store.locality_id ?? '',
    });
    setSelectedFile(null);
    setRemoveRequested(false);
    setLogoError(null);
    setSubmitError(null);
  }, [reset, store.description, store.id, store.locality_id, store.logo_url, store.name]);

  const sortedLocalities = useMemo(
    () =>
      [...localities].sort((a, b) =>
        getLocalityLabel(a, lang).localeCompare(getLocalityLabel(b, lang), localeForSort(lang)),
      ),
    [lang, localities],
  );

  useEffect(() => {
    if (!store.locality_id) return;
    if (!localities.some((locality) => locality.id === store.locality_id)) return;
    setValue('localityId', store.locality_id, { shouldDirty: false, shouldValidate: false });
  }, [localities, setValue, store.locality_id]);

  const onSubmit: SubmitHandler<StoreDetailsFormValues> = async (values) => {
    setSubmitError(null);
    setLogoError(null);

    let logoChange: StoreLogoChange = { kind: 'keep' };
    if (removeRequested) {
      logoChange = { kind: 'remove' };
    } else if (selectedFile) {
      try {
        logoChange = {
          kind: 'replace',
          staged: await stageStoreLogoReplacement(supabase, store.id, selectedFile),
        };
      } catch (err) {
        setLogoError('admin.storeSettings.errors.logoUploadFailed');
        console.error('[StoreDetailsForm.stageStoreLogoReplacement]', err);
        return;
      }
    }

    try {
      await updateMutation.mutateAsync({
        storeId: store.id,
        name: values.name,
        description: values.description,
        localityId: values.localityId,
        logoChange,
      });
    } catch (err) {
      if (logoChange.kind === 'replace') {
        await deleteStoreLogoObject(supabase, logoChange.staged.path);
      }
      setSubmitError(mapStoreDetailsError(err));
      return;
    }

    const cleanupTarget =
      logoChange.kind === 'remove' ||
      (logoChange.kind === 'replace' && store.logo_url !== logoChange.staged.publicUrl)
        ? store.logo_url
        : null;
    try {
      await deleteStoreLogoByUrl(supabase, store.id, cleanupTarget);
    } catch (err) {
      console.error('[StoreDetailsForm.deleteStoreLogoByUrl]', err);
    }

    toast.success(t('admin.storeSettings.toasts.detailsSaved'));
    reset(values);
    setSelectedFile(null);
    setRemoveRequested(false);
  };

  const descriptionLength = watch('description').length;
  const canSubmit =
    !updateMutation.isPending && (isDirty || selectedFile !== null || removeRequested);

  return (
    <form
      noValidate
      dir={dir}
      onSubmit={(event: FormEvent<HTMLFormElement>) => void handleSubmit(onSubmit)(event)}
      className="mt-8 space-y-6"
      data-testid="store-details-form"
    >
      <section className="bg-card border-border rounded-xl border p-6 shadow-sm sm:p-8">
        <h2 className="font-display text-foreground text-start text-xl font-bold">
          {t('admin.storeSettings.details.title')}
        </h2>
        <p className="text-muted-foreground mt-2 text-start text-sm">
          {t('admin.storeSettings.details.description')}
        </p>

        <div className="bg-border my-4 h-px" aria-hidden="true" />

        <div className="space-y-5">
          <div>
            <Label htmlFor="store-details-name" className="text-foreground">
              {t('admin.storeSettings.details.nameLabel')}
            </Label>
            <Input
              id="store-details-name"
              type="text"
              autoComplete="organization"
              placeholder={t('admin.storeSettings.details.namePlaceholder')}
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? 'store-details-name-error' : undefined}
              data-testid="store-details-name"
              {...register('name')}
            />
            {errors.name ? (
              <p
                id="store-details-name-error"
                role="alert"
                className="text-destructive mt-1 text-start text-sm"
                data-testid="store-details-name-error"
              >
                {t(errors.name.message as TranslationKey)}
              </p>
            ) : null}
          </div>

          <div>
            <Label htmlFor="store-details-description" className="text-foreground">
              {t('admin.storeSettings.details.descriptionLabel')}
            </Label>
            <Textarea
              id="store-details-description"
              rows={4}
              maxLength={STORE_DESCRIPTION_MAX_LENGTH}
              placeholder={t('admin.storeSettings.details.descriptionPlaceholder')}
              aria-invalid={!!errors.description}
              aria-describedby={errors.description ? 'store-details-description-error' : undefined}
              data-testid="store-details-description"
              {...register('description')}
            />
            <div className="mt-1 flex items-center justify-between gap-3">
              {errors.description ? (
                <p
                  id="store-details-description-error"
                  role="alert"
                  className="text-destructive text-start text-sm"
                  data-testid="store-details-description-error"
                >
                  {t(errors.description.message as TranslationKey)}
                </p>
              ) : (
                <span />
              )}
              <span className="text-muted-foreground shrink-0 text-xs">
                {t('admin.storeSettings.details.descriptionCount', {
                  count: descriptionLength,
                  max: STORE_DESCRIPTION_MAX_LENGTH,
                })}
              </span>
            </div>
          </div>

          <div>
            <Label htmlFor="store-details-locality" className="text-foreground">
              {t('admin.storeSettings.details.localityLabel')}
            </Label>
            <div className="relative mt-2">
              <select
                id="store-details-locality"
                className="border-input bg-background text-foreground ring-offset-background focus-visible:ring-ring h-10 w-full appearance-none rounded-md border bg-none px-3 py-2 pe-10 text-start text-base [-moz-appearance:none] [-webkit-appearance:none] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 md:text-sm"
                aria-invalid={!!errors.localityId}
                aria-describedby={
                  errors.localityId ? 'store-details-locality-error' : 'store-details-locality-hint'
                }
                disabled={localitiesLoading || localitiesError}
                data-testid="store-details-locality"
                {...register('localityId')}
              >
                <option value="">
                  {localitiesLoading
                    ? t('admin.storeSettings.details.localityLoading')
                    : t('admin.storeSettings.details.localityPlaceholder')}
                </option>
                {sortedLocalities.map((locality) => (
                  <option key={locality.id} value={locality.id}>
                    {getLocalityLabel(locality, lang)}
                  </option>
                ))}
              </select>
              <ChevronDown
                className="text-muted-foreground pointer-events-none absolute end-3 top-1/2 size-4 -translate-y-1/2"
                aria-hidden
              />
            </div>
            {errors.localityId ? (
              <p
                id="store-details-locality-error"
                role="alert"
                className="text-destructive mt-1 text-start text-sm"
                data-testid="store-details-locality-error"
              >
                {t(errors.localityId.message as TranslationKey)}
              </p>
            ) : localitiesError ? (
              <p
                id="store-details-locality-hint"
                role="alert"
                className="text-destructive mt-1 text-start text-sm"
                data-testid="store-details-locality-load-error"
              >
                {t('common.errors.networkFailed')}
              </p>
            ) : (
              <p
                id="store-details-locality-hint"
                className="text-muted-foreground mt-1 text-start text-sm"
              >
                {t('admin.storeSettings.details.localityHint')}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="store-details-url" className="text-foreground">
              {t('admin.storeSettings.details.slugLabel')}
            </Label>
            <Input
              id="store-details-url"
              type="text"
              readOnly
              dir="ltr"
              value={publicUrl}
              className="font-mono"
              data-testid="store-details-url"
            />
            <p className="text-muted-foreground mt-2 flex items-start gap-2 text-start text-sm">
              <Lock className="mt-0.5 size-4 shrink-0" aria-hidden />
              <span>{t('admin.storeSettings.details.slugLockedWarning')}</span>
            </p>
          </div>

          <div>
            <Label className="text-foreground">{t('admin.storeSettings.details.logoLabel')}</Label>
            <div className="mt-2">
              <StoreLogoField
                currentLogoUrl={store.logo_url}
                selectedFile={selectedFile}
                removeRequested={removeRequested}
                errorKey={logoError}
                onSelectedFileChange={setSelectedFile}
                onRemoveRequestedChange={setRemoveRequested}
                onErrorChange={setLogoError}
              />
            </div>
          </div>
        </div>

        <Button
          type="submit"
          disabled={!canSubmit}
          aria-disabled={!canSubmit}
          className="mt-8 w-full sm:w-auto"
          data-testid="store-details-submit"
        >
          {updateMutation.isPending ? (
            <>
              <Loader2 className="me-2 size-4 animate-spin" aria-hidden />
              {t('admin.storeSettings.details.submitting')}
            </>
          ) : (
            t('admin.storeSettings.details.submit')
          )}
        </Button>
      </section>

      {submitError ? (
        <div
          role="alert"
          data-testid="store-details-submit-error"
          data-error-key={submitError}
          className="border-destructive/30 bg-destructive/10 text-destructive flex items-start gap-2 rounded-lg border p-3 text-start text-sm"
        >
          <AlertCircle aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
          <p>{t(submitError as TranslationKey)}</p>
        </div>
      ) : null}
    </form>
  );
}

function getLocalityLabel(locality: Locality, lang: string): string {
  if (lang === 'he') return locality.name_he;
  if (lang === 'en') return locality.name_en ?? locality.name_he;
  return locality.name_ar ?? locality.name_he ?? locality.name_en ?? String(locality.symbol_number);
}

function localeForSort(lang: string): string {
  if (lang === 'he') return 'he';
  if (lang === 'en') return 'en';
  return 'ar';
}
